/**
 * @file Web Gallery Blog Listing container.
 */

import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import get from 'lodash.get';
import { connect } from 'react-redux';
import produce from 'immer';

import WebGalleryBlogListing from '../components/WebGalleryBlogListing';
import {
  AlbumFolderForWebGalleries,
  AlbumFolderWithBlogPosts,
} from '../graphql/queries/album-folder';
import {
  AlbumWithBlogPosts,
  AlbumForWebGalleries,
  AlbumPhotoGroupWithBlogPosts,
} from '../graphql/queries/album';
import { APOLLO_NETWORK_STATUS } from '../../../constants/apollo-network-status';
import { BLOG_POST_FETCH_LIMIT } from '../../../constants/blog';
import { blogPostViewsCreate } from '../graphql/mutations/blog';

const withAlbumData = graphql(AlbumForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    entity: album,
    loadingEntity: loading,
  }),
  skip: ({ match }) => !match.params.albumId || match.params.photoGroupId,
});

const withAlbumFolderData = graphql(AlbumFolderForWebGalleries, {
  options: ({ match }) => ({
    variables: {
      id: match.params.folderId,
    },
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    entity: albumFolder,
    loadingEntity: loading,
  }),
  skip: ({ match }) => !match.params.folderId,
});

const withAlbumFolderWithBlogPosts = graphql(AlbumFolderWithBlogPosts, {
  options: ({ match }) => ({
    variables: {
      id: match.params.folderId,
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { albumFolder, networkStatus, fetchMore } }) => ({
    blogPosts: get(albumFolder, 'linked_blog_posts.records', []),
    total: get(albumFolder, 'linked_blog_posts.total', 0),
    loading: networkStatus === APOLLO_NETWORK_STATUS.LOADING,
    fetchingMore: networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE,
    fetchMore: () =>
      fetchMore({
        variables: {
          offset: albumFolder.linked_blog_posts.count,
          limit: BLOG_POST_FETCH_LIMIT,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prevResult;
          }

          const prevCount = prevResult.albumFolder.linked_blog_posts.count;
          const nextCount = fetchMoreResult.albumFolder.linked_blog_posts.count;

          return {
            ...prevResult,
            albumFolder: {
              ...prevResult.albumFolder,
              linked_blog_posts: {
                ...prevResult.albumFolder.linked_blog_post,
                count: prevCount + nextCount,
                records: [
                  ...prevResult.albumFolder.linked_blog_posts.records,
                  ...fetchMoreResult.albumFolder.linked_blog_posts.records,
                ],
              },
            },
          };
        },
      }),
  }),
  skip: ({ match }) => !match.params.folderId,
});

const withAlbumWithBlogPosts = graphql(AlbumWithBlogPosts, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { album, networkStatus, fetchMore } }) => ({
    blogPosts: get(album, 'linked_blog_posts.records', []),
    total: get(album, 'linked_blog_posts.total', 0),
    loading: networkStatus === APOLLO_NETWORK_STATUS.LOADING,
    fetchingMore: networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE,
    fetchMore: () =>
      fetchMore({
        variables: {
          offset: album.linked_blog_posts.count,
          limit: BLOG_POST_FETCH_LIMIT,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prevResult;
          }

          const prevCount = prevResult.album.linked_blog_posts.count;
          const nextCount = fetchMoreResult.album.linked_blog_posts.count;

          return {
            ...prevResult,
            album: {
              ...prevResult.album,
              linked_blog_posts: {
                ...prevResult.album.linked_blog_post,
                count: prevCount + nextCount,
                records: [
                  ...prevResult.album.linked_blog_posts.records,
                  ...fetchMoreResult.album.linked_blog_posts.records,
                ],
              },
            },
          };
        },
      }),
  }),
  skip: ({ match }) => !match.params.albumId || match.params.photoGroupId,
});

const withAlbumPhotoGroupWithBlogPosts = graphql(AlbumPhotoGroupWithBlogPosts, {
  options: ({ match }) => ({
    variables: {
      id: match.params.photoGroupId,
    },
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { albumPhotoGroup, networkStatus, fetchMore } }) => ({
    entity: albumPhotoGroup,
    blogPosts: get(albumPhotoGroup, 'linked_blog_posts.records', []),
    total: get(albumPhotoGroup, 'linked_blog_posts.total', 0),
    loading: networkStatus === APOLLO_NETWORK_STATUS.LOADING,
    fetchingMore: networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE,
    fetchMore: () =>
      fetchMore({
        variables: {
          offset: albumPhotoGroup.linked_blog_posts.count,
          limit: BLOG_POST_FETCH_LIMIT,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prevResult;
          }

          const prevCount = prevResult.albumPhotoGroup.linked_blog_posts.count;
          const nextCount =
            fetchMoreResult.albumPhotoGroup.linked_blog_posts.count;

          return {
            ...prevResult,
            albumPhotoGroup: {
              ...prevResult.albumPhotoGroup,
              linked_blog_posts: {
                ...prevResult.albumPhotoGroup.linked_blog_post,
                count: prevCount + nextCount,
                records: [
                  ...prevResult.albumPhotoGroup.linked_blog_posts.records,
                  ...fetchMoreResult.albumPhotoGroup.linked_blog_posts.records,
                ],
              },
            },
          };
        },
      }),
  }),
  skip: ({ match }) => !match.params.photoGroupId,
});

const withBlogPostViewsCreate = graphql(blogPostViewsCreate, {
  props: ({ mutate, ownProps }) => ({
    blogPostViewsCreate: blogPostIds =>
      mutate({
        variables: {
          input: {
            blog_post_ids: blogPostIds,
            platform: 'WEB',
          },
        },
        update: store => {
          const { match } = ownProps;
          try {
            if (!match.params.albumFolderId) {
              throw new Error('No Album Folder ID found in Params');
            }
            // Album Folder
            const data = store.readQuery({
              query: AlbumFolderWithBlogPosts,
              variables: {
                id: match.params.folderId,
              },
            });

            const updatedData = produce(data, draft => {
              draft.albumFolder.linked_blog_posts.records =
                data.albumFolder.linked_blog_posts.records.map(blogPost =>
                  blogPostIds.includes(blogPost.id)
                    ? {
                        ...blogPost,
                        is_viewed: true,
                      }
                    : blogPost,
                );
            });

            store.writeQuery({
              query: AlbumFolderWithBlogPosts,
              variables: {
                id: match.params.folderId,
              },
              data: updatedData,
            });
          } catch (error) {}

          try {
            // Album
            if (!match.params.albumId || match.params.photoGroupId) {
              throw new Error('No Album ID found in Params');
            }

            const data = store.readQuery({
              query: AlbumWithBlogPosts,
              variables: {
                id: match.params.albumId,
              },
            });
            const updatedData = produce(data, draft => {
              draft.album.linked_blog_posts.records =
                data.album.linked_blog_posts.records.map(blogPost =>
                  blogPostIds.includes(blogPost.id)
                    ? {
                        ...blogPost,
                        is_viewed: true,
                      }
                    : blogPost,
                );
            });

            store.writeQuery({
              query: AlbumWithBlogPosts,
              variables: {
                id: match.params.albumId,
              },
              data: updatedData,
            });
          } catch (error) {}

          try {
            // Album Photo Group
            if (!match.params.photoGroupId) {
              throw new Error('No Photo Group ID found in Params');
            }

            const data = store.readQuery({
              query: AlbumPhotoGroupWithBlogPosts,
              variables: {
                id: match.params.photoGroupId,
              },
            });

            const updatedData = produce(data, draft => {
              draft.albumPhotoGroup.linked_blog_posts.records =
                data.albumPhotoGroup.linked_blog_posts.records.map(blogPost =>
                  blogPostIds.includes(blogPost.id)
                    ? {
                        ...blogPost,
                        is_viewed: true,
                      }
                    : blogPost,
                );
            });

            store.writeQuery({
              query: AlbumPhotoGroupWithBlogPosts,
              variables: {
                id: match.params.photoGroupId,
              },
              data: updatedData,
            });
          } catch (error) {}
        },
      }),
  }),
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

export default compose(
  connect(mapStateToProps),
  withRouter,
  withAlbumData,
  withAlbumFolderData,
  withAlbumFolderWithBlogPosts,
  withAlbumWithBlogPosts,
  withAlbumPhotoGroupWithBlogPosts,
  withBlogPostViewsCreate,
)(WebGalleryBlogListing);
